<script setup>
import { computed, onMounted } from "vue";
import { useRoute } from "vue-router";
import { CheckCircle2, Laptop, XCircle } from "lucide-vue-next";
import AppHeader from "@/components/layout/AppHeader.vue";
import LoginPanel from "@/components/common/LoginPanel.vue";
import { BRAND_LOGO_URL } from "@/shared/config/constants";
import { formatUtc8DateTime } from "@/shared/utils/format";
import { useAuthStore } from "@/stores/auth";
import { useNativeApprovalStore } from "@/stores/native-approval";
import { nativeApprovalStateMessage } from "@/features/native-approval/model/approval";

const route = useRoute();
const auth = useAuthStore();
const approval = useNativeApprovalStore();
const code = computed(() => (typeof route.query.code === "string" ? route.query.code : ""));
const redirect = computed(() => `/tokenomics${route.fullPath}`);
const stateMessage = computed(() =>
  nativeApprovalStateMessage({
    code: code.value,
    approved: approval.approved,
    status: approval.status,
    error: approval.error,
  })
);

onMounted(async () => {
  await auth.loadAuth();
  approval.loadSession(code.value);
});
</script>

<template>
  <main class="app-shell approval-shell">
    <AppHeader variant="approval" :login-redirect="redirect" />

    <section class="approval-main">
      <div class="approval-wrap">
        <div class="approval-card">
          <div class="approval-head">
            <div class="approval-chip">
              <Laptop :size="28" :stroke-width="2.2" />
            </div>
            <div>
              <h1>批准 mac app 自动上传</h1>
              <p>批准后，mac app 会获得一个只用于上传 codexbar 日汇总的 token，并保存到本机 keychain。</p>
            </div>
          </div>

          <div class="approval-body">
            <div v-if="stateMessage?.tone === 'error'" class="alert">
              <XCircle :size="16" />
              {{ stateMessage.message }}
            </div>

            <dl v-if="code" class="approval-info">
              <dt>设备</dt>
              <dd>{{ approval.deviceName || "Mac App" }}</dd>
              <dt>状态</dt>
              <dd>
                <span class="approval-status" :class="approval.approved ? 'approved' : 'pending'">
                  {{ approval.approved ? "已批准" : approval.status === "expired" ? "已过期" : "等待批准" }}
                </span>
              </dd>
              <dt>过期时间</dt>
              <dd class="mono">{{ approval.expiresAt ? formatUtc8DateTime(approval.expiresAt) : "-" }}</dd>
            </dl>

            <div v-if="!auth.loaded" class="loading-line">正在检查登录态...</div>
            <LoginPanel v-else-if="!auth.user" :redirect="redirect" />
            <div v-else-if="approval.approved" class="approval-success">
              <CheckCircle2 :size="20" :stroke-width="2.6" />
              {{ stateMessage?.message || "已批准。回到 mac app 后会自动完成连接。" }}
            </div>
            <template v-else>
              <button
                type="button"
                class="approval-btn"
                :disabled="!code || approval.approving || approval.status === 'expired'"
                @click="approval.approveDevice(code)"
              >
                {{ approval.approving ? "正在批准..." : "批准这台 mac" }}
              </button>
              <p class="approval-note">token 明文只返回给 app 一次，不会在网页展示。</p>
            </template>
          </div>
        </div>

        <div class="approval-foot">
          <img :src="BRAND_LOGO_URL" alt="" />
          <span>学 AI，做中学</span>
        </div>
      </div>
    </section>
  </main>
</template>
